import { Feedback, useMutationCreateFeedback } from "@apis:feedback/feedback";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { Textarea } from "@components:Textarea";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Slider,
  Switch,
} from "@mui/material";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { useToasterStoreActions } from "@stores:global";
import React, { useState } from "react";

const initialFeedback = {
  difficulty: "easy",
  isAnswerCorrect: true,
  areReferencesCorrect: true,
  comment: "",
  messageId: "",
} as Feedback["POST"]["RequestBody"];

const difficultyOptions = [
  {
    value: 0,
    label: "easy",
    star: 1,
    color: "green",
  },
  {
    value: 50,
    label: "medium",
    star: 2,
    color: "orange",
  },
  {
    value: 100,
    label: "hard",
    star: 3,
    color: "red",
  },
];

interface StarRatingProps {
  difficult: "easy" | "medium" | "hard";
}

const StarRating = ({ difficult }: StarRatingProps) => {
  const numberOfStars = difficultyOptions.find(
    (option) => option.label === difficult,
  )?.star as number;

  return (
    <Box
      sx={{
        color: difficultyOptions.find((option) => option.label === difficult)
          ?.color,
      }}
    >
      {[...Array(numberOfStars)].map((_, index) => (
        <SVGInternalIcon icon="starIcon" key={index} />
      ))}
    </Box>
  );
};

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

interface TalkFeedbackProps {
  messageId: string;
}

export const TalkFeedback = ({ messageId }: TalkFeedbackProps) => {
  const [open, setOpen] = useState(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | undefined>(
    undefined,
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFeedback(initialFeedback);
  };
  const [feedback, setFeedback] = React.useState<
    Feedback["POST"]["RequestBody"]
  >({
    ...initialFeedback,
    messageId,
  });

  const toastActions = useToasterStoreActions();

  const feedbackMutation = useMutationCreateFeedback();

  const handleSubmit = async () => {
    await toastActions.setToasterPromise(
      feedbackMutation.mutateAsync(feedback),
      {
        onPending: () => ({
          message: "Sending feedback...",
        }),
        onSuccess: () => ({
          message: "Feedback sent successfully",
        }),
        onError: () => ({
          message: "Failed to send feedback",
        }),
      },
    );
    handleClose();
  };

  return (
    <Box
      sx={{
        textAlign: "end",
      }}
    >
      <Box>
        <Tooltip title="Good response">
          <IconButton
            size="small"
            onClick={() => {
              setFeedback((prevState) => ({
                ...prevState,
                isAnswerCorrect: true,
              }));
              handleOpen();
              setIsAnswerCorrect(true);
            }}
            sx={{
              "&:hover": {
                "& .like-icon": {
                  fill: "green",
                },
              },
              color: isAnswerCorrect === true ? "green" : undefined,
            }}
          >
            <SVGInternalIcon
              icon="likeIcon"
              sx={{
                height: 20,
                width: 20,
              }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bad response">
          <IconButton
            size="small"
            onClick={() => {
              setFeedback((prevState) => ({
                ...prevState,
                isAnswerCorrect: false,
              }));
              handleOpen();
              setIsAnswerCorrect(false);
            }}
            sx={{
              "&:hover": {
                "& .dislike-icon": {
                  fill: "red",
                },
              },
              color: isAnswerCorrect === false ? "red" : undefined,
            }}
          >
            <SVGInternalIcon
              icon="dislikeIcon"
              sx={{
                height: 20,
                width: 20,
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
          setIsAnswerCorrect(undefined);
        }}
        sx={{
          "& .MuiPaper-root": {
            padding: 0,
          },
        }}
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            boxShadow: 24,
            borderRadius: 5,
          }}
        >
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={2}>
                <span>Send Feedback to Dory</span>
                {isAnswerCorrect === true ? (
                  <SVGInternalIcon
                    icon="likeIcon"
                    sx={{
                      height: 20,
                      width: 20,
                      "& .like-icon": {
                        fill: "green",
                      },
                    }}
                  />
                ) : (
                  <SVGInternalIcon
                    icon="dislikeIcon"
                    sx={{
                      height: 20,
                      width: 20,
                      "& .dislike-icon": {
                        fill: "red",
                      },
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              px: 3,
            }}
          />
          <Divider />
          <CardContent
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            <Box>
              <Box display="flex" alignItems="center" gap={2}>
                <span>Request difficulty</span>
                <StarRating difficult={feedback.difficulty} />
              </Box>

              <Box
                sx={{
                  width: 500,
                  mx: "auto",
                  pt: 2,
                }}
              >
                <Slider
                  defaultValue={0}
                  onChange={(_e, value) => {
                    setFeedback((prevState) => ({
                      ...prevState,
                      difficulty: difficultyOptions.find(
                        (option) => option.value === value,
                      )?.label as "easy" | "medium" | "hard",
                    }));
                  }}
                  valueLabelFormat={(value) => {
                    return difficultyOptions.find(
                      (option) => option.value === value,
                    )?.label;
                  }}
                  step={null}
                  marks={difficultyOptions}
                />
              </Box>
            </Box>
            <Box
              sx={{
                pt: 2,
              }}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <span>
                Do the provided references contain the required information?
              </span>
              <Android12Switch
                data-cy="switch-references"
                checked={feedback.areReferencesCorrect}
                onChange={(e) => {
                  setFeedback((prevState) => ({
                    ...prevState,
                    areReferencesCorrect: e.target.checked,
                  }));
                }}
              />
            </Box>
            <Box
              sx={{
                pt: 2,
              }}
            >
              <Textarea
                value={feedback.comment || ""}
                onChange={(e) =>
                  setFeedback((prevState) => ({
                    ...prevState,
                    comment: e?.target?.value,
                  }))
                }
                minRows={4}
                placeholder="(Optional) Other information that could help us improve our system"
              />
            </Box>
          </CardContent>
          <CardActions
            sx={{
              justifyContent: "flex-end",
              p: 3,
              pt: 0,
            }}
          >
            <Button
              data-cy="button-feedback-submit"
              variant="contained"
              sx={{
                color: "white",
              }}
              onClick={async () => {
                await handleSubmit();
              }}
            >
              Submit
            </Button>
          </CardActions>
        </Card>
      </Modal>
    </Box>
  );
};
